import React, { useState, useEffect } from 'react'
import { useAuth } from '../../hooks/useAuth'
import { useCare } from '../../hooks/useCare'
import { useInviteLink } from '../../hooks/useInviteLink'
import { CareCase } from '../../types/user'

type OnboardingStep = 'welcome' | 'choose-role' | 'choose-profile-type' | 'create-case' | 'join-case' | 'processing'

const OnboardingFlow: React.FC = () => {
  const { user } = useAuth()
  const { createCase, joinWithCode, loadUserCases } = useCare()
  const { inviteCode: urlInviteCode, isFromUrl, processInvite, clearInvite } = useInviteLink()
  const [currentStep, setCurrentStep] = useState<OnboardingStep>('welcome')
  const [userChoice, setUserChoice] = useState<'create-caregiver' | 'create-assisted' | 'join-existing' | null>(null)
  const [profileType, setProfileType] = useState<'human' | 'pet'>('human')
  const [loading, setLoading] = useState(false)
  const [inviteCode, setInviteCode] = useState('')
  const [newCaseData, setNewCaseData] = useState({
    name: '',
    description: '',
    profileName: '',
    profileAge: '',
    profileGender: '' as 'male' | 'female' | ''
  })

  // Verificar si hay un código de invitación detectado automáticamente
  useEffect(() => {
    if (urlInviteCode && isFromUrl) {
      setInviteCode(urlInviteCode)
      setUserChoice('join-existing')
      setCurrentStep('join-case')
    }
  }, [urlInviteCode, isFromUrl])

  const handleCreateCase = async () => {
    if (!newCaseData.name || !newCaseData.profileName) {
      alert('Por favor completa todos los campos requeridos')
      return
    }

    setLoading(true)
    try {
      const caseData: Omit<CareCase, 'id' | 'adminId' | 'createdAt' | 'updatedAt'> = {
        name: newCaseData.name,
        description: newCaseData.description,
        profileType: profileType,
        profileInfo: {
          name: newCaseData.profileName,
          age: newCaseData.profileAge ? parseInt(newCaseData.profileAge) : undefined,
          gender: newCaseData.profileGender || undefined,
          avatarSeed: newCaseData.profileName,
          avatarStyle: 'avataaars'
        },
        isActive: true,
        inviteCode: '',
        settings: {
          allowGuestAccess: false,
          requireApprovalForNewMembers: true,
          notificationSettings: {
            adminGetsAllNotifications: true,
            membersCanReceiveNotifications: true
          }
        }
      }

      await createCase(caseData)
      await loadUserCases()

    } catch (error) {
      console.error('Error creating case:', error)
      alert('Error al crear el caso')
    } finally {
      setLoading(false)
    }
  }

  const handleJoinCase = async () => {
    const codeToUse = inviteCode.trim().toUpperCase()

    if (!codeToUse) {
      alert('Por favor ingresa el código de invitación')
      return
    }

    setLoading(true)
    try {
      if (urlInviteCode && isFromUrl) {
        // Si el código viene de la URL, usar el hook para procesarlo
        await processInvite(async (code) => {
          await joinWithCode(code)
          await loadUserCases()
        })
      } else {
        // Si el código fue ingresado manualmente
        await joinWithCode(codeToUse)
        await loadUserCases()
      }
    } catch (error) {
      console.error('Error joining case:', error)
      alert('Código de invitación inválido o expirado')
    } finally {
      setLoading(false)
    }
  }

  // Componente Welcome
  const renderWelcomeStep = () => (
    <div className="text-center">
      <div className="mb-8">
        <div className="w-24 h-24 mx-auto mb-4 bg-primary rounded-full flex items-center justify-center">
          <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
          </svg>
        </div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          ¡Bienvenido a Mi Medicación!
        </h1>
        <p className="text-lg text-gray-600 dark:text-gray-300">
          Hola {user?.displayName || 'Usuario'}, vamos a configurar tu experiencia
        </p>
      </div>

      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-6 mb-8">
        <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">
          ¿Qué puedes hacer con Mi Medicación?
        </h3>
        <div className="grid md:grid-cols-3 gap-4 text-sm">
          <div className="text-blue-800 dark:text-blue-200">
            <div className="font-medium">💊 Gestionar Medicamentos</div>
            <div>Organiza horarios y dosis</div>
          </div>
          <div className="text-blue-800 dark:text-blue-200">
            <div className="font-medium">👥 Trabajo en Equipo</div>
            <div>Colabora con familiares y cuidadores</div>
          </div>
          <div className="text-blue-800 dark:text-blue-200">
            <div className="font-medium">📱 Recordatorios</div>
            <div>Nunca olvides una dosis</div>
          </div>
        </div>
      </div>

      <button
        onClick={() => setCurrentStep('choose-role')}
        className="btn btn-primary px-8 py-3 text-lg"
      >
        Comenzar Configuración
      </button>
    </div>
  )

  // Componente Choose Role
  const renderChooseRoleStep = () => (
    <div>
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          ¿Cómo vas a usar Mi Medicación?
        </h2>
        <p className="text-gray-600 dark:text-gray-300">
          Selecciona la opción que mejor describe tu situación
        </p>
      </div>

      <div className="grid gap-4 mb-8">
        {/* Opción: Crear caso como cuidador */}
        <button
          onClick={() => {
            setUserChoice('create-caregiver')
            setCurrentStep('choose-profile-type')
          }}
          className="p-6 border-2 border-gray-200 dark:border-gray-600 rounded-lg hover:border-primary hover:bg-primary/5 transition-colors text-left"
        >
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                Soy un Cuidador
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Voy a gestionar los medicamentos de otra persona (familiar, paciente, etc.)
              </p>
            </div>
          </div>
        </button>

        {/* Opción: Crear caso como persona asistida */}
        <button
          onClick={() => {
            setUserChoice('create-assisted')
            setProfileType('human')
            setCurrentStep('create-case')
          }}
          className="p-6 border-2 border-gray-200 dark:border-gray-600 rounded-lg hover:border-primary hover:bg-primary/5 transition-colors text-left"
        >
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                Gestiono mis Propios Medicamentos
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Quiero organizar y llevar el control de mis medicamentos personales
              </p>
            </div>
          </div>
        </button>

        {/* Opción: Unirse a caso existente */}
        <button
          onClick={() => {
            setUserChoice('join-existing')
            setCurrentStep('join-case')
          }}
          className="p-6 border-2 border-gray-200 dark:border-gray-600 rounded-lg hover:border-primary hover:bg-primary/5 transition-colors text-left"
        >
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                Unirme a un Caso Existente
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Tengo un código de invitación para unirme a un equipo de cuidado
              </p>
            </div>
          </div>
        </button>
      </div>

      <div className="text-center">
        <button
          onClick={() => setCurrentStep('welcome')}
          className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        >
          ← Volver
        </button>
      </div>
    </div>
  )

  // Componente Choose Profile Type
  const renderChooseProfileTypeStep = () => (
    <div>
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          ¿A quién vas a cuidar?
        </h2>
        <p className="text-gray-600 dark:text-gray-300">
          Selecciona el tipo de perfil que deseas crear
        </p>
      </div>

      <div className="grid gap-4 mb-8">
        <button
          onClick={() => {
            setProfileType('human')
            setCurrentStep('create-case')
          }}
          className="p-6 border-2 border-gray-200 dark:border-gray-600 rounded-lg hover:border-primary hover:bg-primary/5 transition-colors text-left"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
              <span className="text-2xl">👤</span>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white">
                A una Persona
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Familiar, amigo o paciente
              </p>
            </div>
          </div>
        </button>

        <button
          onClick={() => {
            setProfileType('pet')
            setCurrentStep('create-case')
          }}
          className="p-6 border-2 border-gray-200 dark:border-gray-600 rounded-lg hover:border-primary hover:bg-primary/5 transition-colors text-left"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center">
              <span className="text-2xl">🐾</span>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white">
                A una Mascota
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Perro, gato u otra mascota
              </p>
            </div>
          </div>
        </button>
      </div>

      <div className="text-center">
        <button
          onClick={() => setCurrentStep('choose-role')}
          className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        >
          ← Volver
        </button>
      </div>
    </div>
  )

  // Componente Create Case
  const renderCreateCaseStep = () => (
    <div>
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          {userChoice === 'create-caregiver' ? 'Configurar Caso de Cuidado' : 'Configurar tu Medicación Personal'}
        </h2>
        <p className="text-gray-600 dark:text-gray-300">
          {userChoice === 'create-caregiver'
            ? (profileType === 'pet' ? 'Configura el perfil de tu mascota' : 'Proporciona información sobre la persona que vas a cuidar')
            : 'Configura tu perfil personal de medicación'
          }
        </p>
      </div>

      <form onSubmit={(e) => { e.preventDefault(); handleCreateCase(); }} className="space-y-6 max-w-md mx-auto">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {userChoice === 'create-caregiver' ? (profileType === 'pet' ? 'Nombre de la mascota' : 'Nombre de la persona bajo cuidado') : 'Tu nombre completo'}
          </label>
          <input
            type="text"
            value={newCaseData.profileName}
            onChange={(e) => {
              const name = e.target.value
              let caseName = ''
              if (userChoice === 'create-caregiver') {
                caseName = name ? `Cuidado de ${name}` : ''
              } else {
                caseName = 'Mis Medicamentos'
              }
              setNewCaseData({ ...newCaseData, profileName: name, name: caseName })
            }}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
            placeholder={userChoice === 'create-caregiver' ? (profileType === 'pet' ? 'Ej: Max' : 'Ej: María García') : user?.displayName || 'Tu nombre'}
            required
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Edad (opcional)
            </label>
            <input
              type="number"
              value={newCaseData.profileAge}
              onChange={(e) => setNewCaseData({ ...newCaseData, profileAge: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
              placeholder="Ej: 65"
              min="1"
              max="120"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {profileType === 'pet' ? 'Sexo (opcional)' : 'Género (opcional)'}
            </label>
            <select
              value={newCaseData.profileGender}
              onChange={(e) => setNewCaseData({ ...newCaseData, profileGender: e.target.value as 'male' | 'female' | '' })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
            >
              <option value="">Seleccionar</option>
              <option value="male">{profileType === 'pet' ? 'Macho' : 'Masculino'}</option>
              <option value="female">{profileType === 'pet' ? 'Hembra' : 'Femenino'}</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Descripción (opcional)
          </label>
          <textarea
            value={newCaseData.description}
            onChange={(e) => setNewCaseData({ ...newCaseData, description: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
            placeholder="Información adicional sobre el caso..."
            rows={3}
          />
        </div>

        <div className="flex gap-3 pt-4">
          <button
            type="button"
            onClick={() => setCurrentStep(userChoice === 'create-caregiver' ? 'choose-profile-type' : 'choose-role')}
            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            ← Volver
          </button>
          <button
            type="submit"
            disabled={loading}
            className="flex-1 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? 'Creando...' : 'Crear Caso'}
          </button>
        </div>
      </form>
    </div>
  )

  // Componente Join Case
  const renderJoinCaseStep = () => (
    <div>
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Unirse a un Caso Existente
        </h2>
        <p className="text-gray-600 dark:text-gray-300">
          Ingresa el código de invitación que recibiste
        </p>
      </div>

      <form onSubmit={(e) => { e.preventDefault(); handleJoinCase(); }} className="space-y-6 max-w-md mx-auto">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Código de Invitación
          </label>
          <input
            type="text"
            value={inviteCode}
            onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white text-center text-lg font-mono"
            placeholder="ABC-123-XYZ"
            required
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            El código fue enviado por el administrador del caso
          </p>
        </div>

        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
          <div className="flex items-center gap-2 text-sm text-blue-800 dark:text-blue-200">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>Una vez validado el código, te unirás automáticamente al equipo de cuidado</span>
          </div>
        </div>

        <div className="flex gap-3 pt-4">
          <button
            type="button"
            onClick={() => setCurrentStep('choose-role')}
            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            ← Volver
          </button>
          <button
            type="submit"
            disabled={loading || !inviteCode.trim()}
            className="flex-1 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? 'Validando...' : 'Unirse al Caso'}
          </button>
        </div>
      </form>
    </div>
  )

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-8 w-full max-w-2xl">
        {currentStep === 'welcome' && renderWelcomeStep()}
        {currentStep === 'choose-role' && renderChooseRoleStep()}
        {currentStep === 'choose-profile-type' && renderChooseProfileTypeStep()}
        {currentStep === 'create-case' && renderCreateCaseStep()}
        {currentStep === 'join-case' && renderJoinCaseStep()}
      </div>
    </div>
  )
}

export default OnboardingFlow 